import React from 'react';
import { StyledAmount, StyledContainer, StyledLabel } from './styles';
import { SettlementItemsQueryParams } from '../../types/apis';
import { useQuery } from '@tanstack/react-query';
import { settlementQuery } from '../../queries';
import { convertSettlementItemsTotal } from '../../utils';
import { isAxiosError } from 'axios';

type Props = {
  request: SettlementItemsQueryParams;
};

type SummaryItemProps = {
  label: string;
  amount?: number;
  unit?: string;
  isEmpty?: boolean;
};

const SummaryItem = ({ label, amount, unit, isEmpty }: SummaryItemProps): JSX.Element => (
  <div>
    <StyledLabel>{label}</StyledLabel>
    <StyledAmount>{isEmpty ? '-' : `${amount?.toLocaleString()}${unit}`}</StyledAmount>
  </div>
);

const SettlementSummaryBox = ({ request }: Props): JSX.Element => {
  const { data, isError, error } = useQuery({
    ...settlementQuery.list(request)._ctx.total,
    select: ({ totalSettlement }) => convertSettlementItemsTotal(totalSettlement),
    keepPreviousData: true,
  });

  if (isError) {
    if (isAxiosError(error)) {
      throw new Error(`SettlementSummaryBox: ${error.message}`);
    }

    throw new Error(`SettlementSummaryBox: ${error}`);
  }

  const {
    totalSettlementAmount,
    totalUsedCouponAmount,
    totalUsedPointAmount,
    totalFee,
    totalPgFee,
    totalAmondzFee,
    totalMarketingFee,
    totalFulfillmentFee,
    totalCount,
    totalSalesAmount,
    totalCouponShareAmount,
  } = data ?? {};

  const isEmpty = !totalCount;

  const summaryItems = [
    { label: '판매금액', amount: totalSalesAmount, unit: '원', isEmpty },
    { label: '정산금액', amount: totalSettlementAmount, unit: '원', isEmpty },
    { label: '쿠폰 사용 금액', amount: totalUsedCouponAmount, unit: '원', isEmpty },
    { label: '쿠폰 분담액(브랜드)', amount: totalCouponShareAmount, unit: '원', isEmpty },
    { label: '포인트 사용 금액', amount: totalUsedPointAmount, unit: '원', isEmpty },
    { label: '수수료 합계 (VAT 포함)', amount: totalFee, unit: '원', isEmpty },
    { label: 'PG사 판매 수수료 (VAT 포함)', amount: totalPgFee, unit: '원', isEmpty },
    { label: 'amondz 판매 수수료 (VAT 포함)', amount: totalAmondzFee, unit: '원', isEmpty },
    { label: '마케팅 수수료 (VAT 포함)', amount: totalMarketingFee, unit: '원', isEmpty },
    { label: '풀필먼트 수수료 (VAT 포함)', amount: totalFulfillmentFee, unit: '원', isEmpty },
  ] as const;

  return (
    <StyledContainer>
      {summaryItems.map(({ label, amount, unit, isEmpty }) => (
        <SummaryItem key={label} label={label} amount={amount} unit={unit} isEmpty={isEmpty} />
      ))}
    </StyledContainer>
  );
};

const Placeholder = (): JSX.Element => {
  const labels = [
    '판매금액',
    '정산금액',
    '쿠폰 사용 금액',
    '쿠폰 분담액(브랜드)',
    '포인트 사용 금액',
    '수수료 합계 (VAT 포함)',
    'PG사 판매 수수료 (VAT 포함)',
    'amondz 판매 수수료 (VAT 포함)',
    '마케팅 수수료 (VAT 포함)',
    '풀필먼트 수수료 (VAT 포함)',
  ];

  return (
    <StyledContainer>
      {labels.map((label) => (
        <SummaryItem key={label} label={label} isEmpty />
      ))}
    </StyledContainer>
  );
};

SettlementSummaryBox.Placeholder = Placeholder;

export default SettlementSummaryBox;
